import React, { useState } from 'react';
import {
  X,
  Smartphone,
  Send,
  CheckCircle2,
  AlertTriangle,
  PhoneCall,
  ShieldAlert,
  Radio,
  Check,
  KeyRound,
  ExternalLink,
  MessageSquare,
  Volume2,
  PhoneIncoming,
  BellOff,
  Ambulance
} from 'lucide-react';

interface EmergencySmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPhone: string;
  onUpdatePhone: (newPhone: string) => void;
}

export const EmergencySmsModal: React.FC<EmergencySmsModalProps> = ({
  isOpen,
  onClose,
  currentPhone,
  onUpdatePhone
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>(currentPhone || '+14083910924');
  const [sendingTest, setSendingTest] = useState<boolean>(false);
  const [callingTest, setCallingTest] = useState<boolean>(false);
  const [ivrSimulating, setIvrSimulating] = useState<string | null>(null);

  const [testResult, setTestResult] = useState<{
    success: boolean;
    provider?: string;
    deliveryStatus?: string;
    message?: string;
    timestamp?: string;
  } | null>(null);

  const [callResult, setCallResult] = useState<{
    success: boolean;
    provider?: string;
    sid?: string;
    callStatus?: string;
    message?: string;
    timestamp?: string;
  } | null>(null);

  const [ivrFeedback, setIvrFeedback] = useState<{
    digit: string;
    action: string;
    message: string;
  } | null>(null);

  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);

  React.useEffect(() => {
    if (!isOpen) return;
    fetch('https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=get_phone')
      .then((r) => r.json())
      .then((data) => {
        if (data && data.phone) {
          setPhoneNumber(data.phone);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSavePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    onUpdatePhone(phoneNumber);
    setSavedSuccess(true);
    try {
      await fetch(
        `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=save_phone&phone=${encodeURIComponent(
          phoneNumber
        )}`
      );
    } catch (err) {
      //
    }
    setTimeout(() => setSavedSuccess(false), 2500);
  };

  const handleSendTestSms = async () => {
    setSendingTest(true);
    setTestResult(null);

    try {
      const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
      const response = await fetch(
        `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=test_sms&to=${encodeURIComponent(
          cleanPhone
        )}`
      );
      const data = await response.json();

      if (data && data.result) {
        setTestResult({
          success: data.result.success,
          provider: data.result.provider,
          deliveryStatus: data.result.deliveryStatus,
          message: data.result.messageText || data.result.error || data.result.note,
          timestamp: data.result.timestamp || new Date().toISOString()
        });
      } else {
        setTestResult({
          success: false,
          deliveryStatus: 'GATEWAY_ERROR',
          message: data.error || 'Failed to dispatch test SMS'
        });
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        deliveryStatus: 'NETWORK_ERROR',
        message: err.message
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleTriggerTestCall = async () => {
    setCallingTest(true);
    setCallResult(null);

    try {
      const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
      const response = await fetch(
        `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=test_call&to=${encodeURIComponent(
          cleanPhone
        )}`
      );
      const data = await response.json();

      if (data && data.result) {
        setCallResult({
          success: data.result.success,
          provider: data.result.provider,
          sid: data.result.sid,
          callStatus: data.result.callStatus,
          message: data.result.spokenScript || (data.result.success ? 'Call placed and ringing' : data.result.error),
          timestamp: data.result.timestamp || new Date().toISOString()
        });
      } else {
        setCallResult({
          success: false,
          callStatus: 'GATEWAY_ERROR',
          message: data.error || 'Failed to trigger emergency voice call'
        });
      }
    } catch (err: any) {
      setCallResult({
        success: false,
        callStatus: 'NETWORK_ERROR',
        message: err.message
      });
    } finally {
      setCallingTest(false);
    }
  };

  const handleSimulateIvrDigit = async (digit: '1' | '2') => {
    setIvrSimulating(digit);
    setIvrFeedback(null);

    try {
      const response = await fetch(
        `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=handle_voice_ivr&Digits=${digit}&room=101&format=json`
      );
      const data = await response.json();

      if (data && data.result) {
        setIvrFeedback({
          digit,
          action: data.result.action,
          message:
            digit === '1'
              ? 'Keypress 1 Processed: Bedside caregiver dispatched to Room 101. Central alarm silenced.'
              : 'Keypress 2 Processed: Critical 911 EMS Trauma response activated.'
        });
      }
    } catch (err: any) {
      setIvrFeedback({
        digit,
        action: 'ERROR',
        message: err.message || 'Failed to simulate IVR keypress'
      });
    } finally {
      setIvrSimulating(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border-2 border-slate-700 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/90">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-indigo-500/20 border border-indigo-500/40 text-indigo-300">
              <PhoneIncoming className="h-6 w-6 text-indigo-400 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-white text-base">
                  Life-Safety Telephony &amp; Interactive IVR Hub
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold uppercase">
                  Twilio IVR Live
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Automated high-priority voice calls with <strong>interactive keypad acknowledgement</strong> on radar fall events.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 text-xs">
          {/* Phone Number Input Form */}
          <form onSubmit={handleSavePhone} className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor="emergency-phone" className="font-bold text-slate-200 text-xs flex items-center gap-1.5">
                <PhoneCall className="h-3.5 w-3.5 text-indigo-400" />
                <span>Primary Emergency Dispatch Mobile Number:</span>
              </label>
              <span className="text-[10px] font-mono text-slate-400">
                On-Duty Nurse / Health Proxy (Sarah Vance)
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                id="emergency-phone"
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-2 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
              />

              <button
                type="submit"
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition flex items-center justify-center gap-1.5"
              >
                {savedSuccess ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : null}
                <span>{savedSuccess ? 'Saved' : 'Save Number'}</span>
              </button>
            </div>

            {/* Action Trigger Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={handleTriggerTestCall}
                disabled={callingTest}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 hover:from-emerald-500 hover:to-indigo-500 text-white font-extrabold text-xs transition flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/50 active:scale-95 disabled:opacity-50"
              >
                <PhoneCall className={`h-4 w-4 ${callingTest ? 'animate-bounce' : 'animate-pulse'}`} />
                <span>{callingTest ? 'Placing Voice Call...' : 'Trigger Emergency Voice Call'}</span>
              </button>

              <button
                type="button"
                onClick={handleSendTestSms}
                disabled={sendingTest}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs border border-slate-700 transition flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
              >
                <Send className={`h-3.5 w-3.5 ${sendingTest ? 'animate-spin' : ''}`} />
                <span>{sendingTest ? 'Transmitting...' : 'Send Test SMS (877 Pending)'}</span>
              </button>
            </div>

            <p className="text-[10.5px] text-slate-400">
              When Room 101 detects an impact fall, the cloud engine places an <strong>audible emergency voice call</strong> directly to this phone number so you never miss an alert.
            </p>
          </form>

          {/* Interactive Keypad IVR Simulation Bar */}
          <div className="p-4 rounded-2xl bg-indigo-950/30 border border-indigo-500/40 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-white text-xs flex items-center gap-1.5">
                <PhoneIncoming className="h-4 w-4 text-indigo-400" />
                <span>Two-Way Keypad Response (During Phone Call)</span>
              </span>
              <span className="text-[9px] font-mono text-indigo-300 bg-indigo-500/20 px-2 py-0.5 rounded border border-indigo-500/40 font-bold uppercase">
                Twilio &lt;Gather&gt; IVR
              </span>
            </div>

            <p className="text-[11px] text-slate-300 leading-relaxed">
              When answering the phone call, you can press numbers on your phone keypad to instruct the trauma hub:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => handleSimulateIvrDigit('1')}
                disabled={ivrSimulating !== null}
                className="p-3 rounded-xl bg-slate-900 border border-emerald-500/50 hover:bg-emerald-950/40 text-left transition flex items-start gap-2.5 group"
              >
                <span className="h-7 w-7 rounded-lg bg-emerald-500 text-slate-950 font-black text-sm flex items-center justify-center shrink-0 shadow-sm">
                  1
                </span>
                <div>
                  <span className="font-bold text-emerald-300 block text-xs">
                    Press 1: Acknowledge &amp; Dispatch Bedside
                  </span>
                  <span className="text-[10.5px] text-slate-400 block mt-0.5">
                    Silences central hospital sirens and updates dashboard to "Caregiver Dispatched".
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSimulateIvrDigit('2')}
                disabled={ivrSimulating !== null}
                className="p-3 rounded-xl bg-slate-900 border border-rose-500/50 hover:bg-rose-950/40 text-left transition flex items-start gap-2.5 group"
              >
                <span className="h-7 w-7 rounded-lg bg-rose-500 text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm">
                  2
                </span>
                <div>
                  <span className="font-bold text-rose-300 block text-xs">
                    Press 2: Escalate to 911 / EMS
                  </span>
                  <span className="text-[10.5px] text-slate-400 block mt-0.5">
                    Mobilizes emergency services and triggers Code Blue on Room 101.
                  </span>
                </div>
              </button>
            </div>

            {ivrFeedback && (
              <div className="p-3 rounded-xl bg-slate-950 border border-indigo-500/40 font-mono text-[11px] text-indigo-200 flex items-center gap-2 animate-fadeIn">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                <span>{ivrFeedback.message}</span>
              </div>
            )}
          </div>

          {/* Test Voice Call Result Feedback */}
          {callResult && (
            <div
              className={`p-4 rounded-2xl border text-xs space-y-2 animate-fadeIn ${
                callResult.success
                  ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-200'
                  : 'bg-rose-950/40 border-rose-500/50 text-rose-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span className="font-bold">
                    {callResult.success ? 'Emergency Voice Call Placed Successfully' : 'Voice Call Dispatch Error'}
                  </span>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold">
                  {callResult.callStatus || 'QUEUED'}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/60 font-mono text-[11px] text-slate-300 space-y-1">
                <div>
                  <span className="text-slate-500">Destination:</span> {phoneNumber}
                </div>
                {callResult.sid && (
                  <div>
                    <span className="text-slate-500">Call SID:</span> {callResult.sid}
                  </div>
                )}
                <div>
                  <span className="text-slate-500">Instructions:</span> Pick up your phone and press <strong>1</strong> or <strong>2</strong> to test the two-way acknowledgment!
                </div>
              </div>
            </div>
          )}

          {/* Test SMS Result Feedback */}
          {testResult && (
            <div
              className={`p-4 rounded-2xl border text-xs space-y-2 animate-fadeIn ${
                testResult.success
                  ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-200'
                  : 'bg-amber-950/40 border-amber-500/50 text-amber-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                  <span className="font-bold">
                    {testResult.success ? 'Cellular SMS Dispatched' : 'Toll-Free Carrier SMS Notice'}
                  </span>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  {testResult.provider || 'CARRIER_GATEWAY'}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/60 font-mono text-[11px] text-slate-300 space-y-1">
                <div>
                  <span className="text-slate-500">Destination:</span> {phoneNumber}
                </div>
                <div>
                  <span className="text-slate-500">Carrier Status:</span> {testResult.deliveryStatus}
                </div>
                {testResult.message && (
                  <div>
                    <span className="text-slate-500">Detail:</span> "{testResult.message}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Real Emergency Voice Script Preview */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <span className="font-bold text-white text-xs flex items-center gap-1.5">
              <Volume2 className="h-3.5 w-3.5 text-teal-400" />
              <span>Voice Escalation Synthesizer (Spoken Aloud to Caregiver Phone):</span>
            </span>

            <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 font-mono text-[11px] text-emerald-300 leading-relaxed">
              "Emergency STAT Alert from MediCore Trauma Hub. A critical fall has been detected in Room 101 for Eleanor Vance by 60 gigahertz radar. Press 1 to acknowledge and dispatch bedside caregiver. Press 2 to escalate to nine one one emergency medical services."
            </div>
          </div>

          {/* Twilio Production Telephony Status */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="font-bold text-white text-xs flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                <span>Twilio Telephony Carrier Channel Status</span>
              </span>
              <span className="text-[9px] font-mono text-emerald-300 bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/40 font-bold">
                CONNECTED
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono">
              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300">
                <span className="text-slate-500 block text-[9.5px]">VOICE CALLS (TTS + IVR)</span>
                <span className="text-emerald-300 font-bold">🟢 ACTIVE (Rings + Keypad Response)</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300">
                <span className="text-slate-500 block text-[9.5px]">SMS MESSAGING</span>
                <span className="text-amber-300 font-bold">🟡 Toll-Free Verification Pending</span>
              </div>
            </div>

            <p className="text-slate-400 text-[10.5px]">
              Toll-Free phone numbers (877) require carrier business verification for outbound SMS, but <strong>Voice Calls and Keypad IVR are 100% active and unblocked</strong>! Voice alerts bypass cellular silent modes and ring your device aloud.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/90 flex items-center justify-between">
          <span className="text-[11px] font-mono text-slate-500">
            Twilio Voice &lt;Gather&gt; IVR REST API • Room 101 Fall Escalation Pipeline
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
